// Pop-up dedicado do Tutor IA. Aberto por `window.open('?view=ai-window&bankId=X', ...)`
// pelo botão pop-out no painel direito do Modo Estudo.
//
// O popup NÃO sabe qual questão o aluno está vendo na página principal —
// ele aprende isso ouvindo o canal "ai-assistant-window-context" e cai em
// fallback de localStorage pra navegadores sem BroadcastChannel.
//
// Sincronia com a página principal:
//   - Mount: dispara "open" no canal "ai-assistant-window" + pinga heartbeat
//     a cada 5s. O AiAssistant inline detecta isso via useAiAssistantWindowOpen
//     e troca o body por um banner "trazer para cá".
//   - Recebe contexto (question + respostaAluno) via BroadcastChannel
//     "ai-assistant-window-context" sempre que o aluno troca de questão.
//
// O chat é local a esta janela (signals do AiAssistant.tsx são por window),
// mas a persistência vai pro mesmo backend, então recarregar reaproveita
// o histórico que foi salvo aqui ou na página principal.

import { useEffect, useState } from "preact/hooks";
import { PictureInPicture2, Sparkles, X } from "lucide-react";
import {
  AI_WINDOW_CONTEXT_CHANNEL,
  AiAssistantBody,
  aiAssistantWindowClose,
  aiAssistantWindowHeartbeat,
  aiAssistantWindowOpen,
  type AiAssistantWindowContext
} from "./AiAssistant";

const HEARTBEAT_MS = 5_000;

function readCtxFromStorage(bankId: string): AiAssistantWindowContext | null {
  try {
    const raw = localStorage.getItem(`ai-assistant-window-ctx:${bankId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.bankId === bankId) {
      return {
        bankId: parsed.bankId,
        question: parsed.question ?? null,
        respostaAluno:
          typeof parsed.respostaAluno === "number" ? parsed.respostaAluno : null
      };
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function AiAssistantWindow({ bankId }: { bankId: string }) {
  // Contexto recebido da janela principal. Hidrata do storage se BroadcastChannel
  // não pegou nada ainda (cobre o caso de refresh do popup).
  const [ctx, setCtx] = useState<AiAssistantWindowContext | null>(() =>
    readCtxFromStorage(bankId)
  );

  // Heartbeat + open/close + listener de contexto.
  useEffect(() => {
    aiAssistantWindowOpen(bankId);
    const heartbeatInt = window.setInterval(() => {
      aiAssistantWindowHeartbeat(bankId);
    }, HEARTBEAT_MS);

    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel(AI_WINDOW_CONTEXT_CHANNEL);
      bc.onmessage = (ev) => {
        const data = ev.data as AiAssistantWindowContext | null;
        if (!data || data.bankId !== bankId) return;
        setCtx({
          bankId: data.bankId,
          question: data.question ?? null,
          respostaAluno: data.respostaAluno ?? null
        });
      };
    } catch {
      /* sem BroadcastChannel — polling abaixo cobre */
    }

    // Polling do storage como fallback (mais lento, mas funciona offline).
    const pollInt = window.setInterval(() => {
      const stored = readCtxFromStorage(bankId);
      if (!stored) return;
      // Aplica só se mudou (id da questão ou resposta diferente).
      setCtx((prev) => {
        if (!prev) return stored;
        if (
          prev.question?.id !== stored.question?.id ||
          prev.respostaAluno !== stored.respostaAluno
        ) {
          return stored;
        }
        return prev;
      });
    }, 2000);

    function onUnload() {
      aiAssistantWindowClose(bankId);
    }
    window.addEventListener("beforeunload", onUnload);
    window.addEventListener("pagehide", onUnload);

    return () => {
      window.clearInterval(heartbeatInt);
      window.clearInterval(pollInt);
      if (bc) bc.close();
      window.removeEventListener("beforeunload", onUnload);
      window.removeEventListener("pagehide", onUnload);
      onUnload();
    };
  }, [bankId]);

  return (
    <div class="flex h-screen w-screen flex-col bg-background text-foreground">
      <header class="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-card/40 px-4 py-2">
        <div class="flex min-w-0 items-center gap-2">
          <div class="grid size-8 shrink-0 place-items-center rounded-md bg-primary/15 text-primary ring-1 ring-primary/20">
            <Sparkles className="size-4" />
          </div>
          <div class="min-w-0 leading-tight">
            <div class="truncate text-sm font-bold">Tutor IA</div>
            <div class="flex items-center gap-1 text-[11px] text-muted-foreground">
              <PictureInPicture2 className="size-3" />
              Janela dedicada · contexto sincronizado com o bloco
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => window.close()}
          title="Fechar janela"
          aria-label="Fechar janela"
          class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </header>
      <main class="flex min-h-0 flex-1 flex-col">
        {ctx?.question ? (
          <AiAssistantBody
            question={ctx.question}
            respostaAluno={ctx.respostaAluno}
            bankId={bankId}
            side="right"
          />
        ) : (
          <div class="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center text-xs text-muted-foreground">
            <Sparkles className="size-5 opacity-60" />
            <p>Volte para a aba do simulador e abra uma questão.</p>
            <p>O contexto aparece aqui automaticamente.</p>
          </div>
        )}
      </main>
    </div>
  );
}
